import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'

import { useSession } from '../../../../../contexts/SessionContext'
import { useDirectDebitStatus } from '../../../../../contexts/session/DirectDebitContext'

import DataTable from '../../../../elements/direct-debit/DataTable'
import Button from '../../../../elements/direct-debit/Button'
import DDIcon from '../../../../icons/DDIcon'
import {
  useReducedMotionPreference,
  DURATION,
  SPRING,
} from '../../../../../styles/motion'
import styles from './MandateCreatedScreen.module.css'

const REDIRECT_COUNTDOWN_SECS = 5
const CONFETTI_COUNT = 7

// Spec screen 07. Redirect-countdown pattern ported from the legacy SuccessScreen.js
// (10s there; spec calls for 5s here). Summary table only includes fields the first
// iteration actually had available (Receiver, Sender, Reference) plus the static status
// copy the spec itself dictates ("Awaiting bank approval", never "active", per design
// principle #4) - Limit/Date limit rows are omitted, not guessed. See final report.
const MandateCreatedScreen = () => {
  const { sessionData } = useSession()
  const { sessionReference, selectedBank, accountNumber } = useDirectDebitStatus()
  const reduceMotion = useReducedMotionPreference()
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECS)

  const redirectUrl = sessionData?.redirectUri

  useEffect(() => {
    if (!redirectUrl) return undefined
    if (countdown === 0) {
      window.location.replace(redirectUrl)
      return undefined
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown, redirectUrl])

  const confettiStars = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => {
        const angle = (i / CONFETTI_COUNT) * Math.PI * 2
        const distance = 44 + Math.random() * 20
        return {
          key: i,
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance,
          icon: i % 2 === 0 ? 'confetti-star-brand' : 'confetti-star-red',
        }
      }),
    [],
  )

  const rows = [
    { label: 'Receiver', value: sessionData?.merchantName || 'Merchant', labelColor: 'secondary' },
    { label: 'Sender', value: `${selectedBank?.bankName || 'Your bank'} ${accountNumber}`, labelColor: 'secondary' },
    { label: 'Reference', value: sessionReference || '', labelColor: 'secondary' },
  ]

  return (
    <div className={styles.body}>
      <div className={styles.successWrap}>
        <motion.div
          className={styles.circle}
          initial={reduceMotion ? false : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: DURATION.successCirclePop, ease: SPRING }}
        >
          <motion.svg width='28' height='28' viewBox='0 0 24 24' fill='none'>
            <motion.path
              d='M5 13l4 4L19 7'
              stroke='white'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
              initial={reduceMotion ? false : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: DURATION.successCheckDraw,
                delay: reduceMotion ? 0 : DURATION.successCheckDelay,
              }}
            />
          </motion.svg>
        </motion.div>
        {!reduceMotion &&
          confettiStars.map((star) => (
            <motion.span
              key={star.key}
              className={styles.confettiStar}
              initial={{ opacity: 0, x: 0, y: 0 }}
              animate={{ opacity: [1, 1, 0], x: star.x, y: star.y }}
              transition={{ duration: DURATION.successConfettiBurst, delay: DURATION.successConfettiDelay }}
            >
              <DDIcon name={star.icon} />
            </motion.span>
          ))}
      </div>

      <motion.div
        className={styles.content}
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: reduceMotion ? 0 : DURATION.successTextFadeUpDelay }}
      >
        <div>
          <h1 className={styles.title}>You are all set</h1>
          <span className={styles.statusPill}>Awaiting bank approval</span>
        </div>
        <DataTable rows={rows} />
      </motion.div>

      <div className={styles.bottom}>
        <Button onClick={() => redirectUrl && window.location.replace(redirectUrl)}>
          Return to {sessionData?.merchantName || 'merchant'} now
        </Button>
        {redirectUrl && (
          <div className={styles.redirectNote} role='status' aria-live='polite'>
            <DDIcon name='spinner' className={styles.redirectSpin} />
            <span>Returning in {countdown}s...</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default MandateCreatedScreen
